import {
  MouseEvent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { SearchBar } from 'components';
import { lodash } from 'helpers';
import { useControlledState } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { Box, Pressed, Typography } from 'libs/ui';
import { Icon } from 'libs/ui/Icon';
import { Colors } from 'libs/ui/theme';

import {
  PopoverStyled,
  SelectStyled,
  PopoverContentStyled,
  SelectFilterLoading,
} from './SelectFilter.styles';

interface Option<T> {
  label: string;
  value: T;
}

type SelectFilterProps<T> = PropsWithChildren<
  {
    title: string;
    activeValues: T[];
    options: Option<T>[];
    enabledSearch?: boolean;
    onChange: (activeValues: T[]) => void;
    loading?: boolean;
  } & (
    | { search?: never; onSearchChange?: never }
    | { search: string; onSearchChange: (value: string) => void }
  )
>;

const COUNT_VISIBLE_OPTIONS = 5;

export const SelectFilter = <T,>({
  title,
  options,
  enabledSearch,
  activeValues,
  children,
  onChange,
  loading,
  search,
  onSearchChange,
}: SelectFilterProps<T>) => {
  const { t } = useTranslation();

  const [collapsed, setCollapsed] = useState(false);

  const [searchValue, handleSearchChange, isSearchControlled] =
    useControlledState<string>(
      search !== undefined
        ? {
            value: search,
            onChange: onSearchChange,
          }
        : { initialValue: '' }
    );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isOpen = Boolean(anchorEl);

  const filterOptions = useMemo(
    () =>
      isSearchControlled
        ? options
        : lodash.searchBy(options, searchValue, (option) => option.label),
    [options, searchValue, isSearchControlled]
  );

  const visibleOptions = useMemo(() => {
    const options = [...filterOptions];

    options.sort((currentOption, nextOption) => {
      const currentActiveIndex = activeValues.findIndex(
        (value) => value === currentOption.value
      );
      const nextActiveIndex = activeValues.findIndex(
        (value) => value === nextOption.value
      );

      if (currentActiveIndex !== -1 && nextActiveIndex === -1) return -1;
      if (currentActiveIndex === -1 && nextActiveIndex !== -1) return 1;
      return currentActiveIndex - nextActiveIndex;
    });

    return !collapsed ? options.slice(0, COUNT_VISIBLE_OPTIONS) : options;
  }, [activeValues, collapsed, filterOptions]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleOnChange = (option: Option<T>) => {
    const isActive = activeValues.includes(option.value);
    let newActiveValues = [...activeValues];

    if (isActive) {
      newActiveValues = newActiveValues.filter(
        (value) => value !== option.value
      );
    } else {
      newActiveValues.push(option.value);
    }

    onChange(newActiveValues);
  };

  useEffect(() => {
    if (!isOpen) {
      handleSearchChange('');
      setCollapsed(false);
    }
  }, [isOpen, handleSearchChange]);

  return (
    <>
      <SelectStyled onClick={handleClick}>
        <Typography variant="captionMedium">{title}</Typography>
        {activeValues.length > 0 && (
          <Box as="span" className="selectFilter-count">
            <Typography className="selectFilter-count-label">
              {activeValues.length}
            </Typography>
          </Box>
        )}
        {isOpen ? <Icon.ArrowUp /> : <Icon.ArrowDown />}
      </SelectStyled>
      <PopoverStyled
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={isOpen}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handleClose}
      >
        <PopoverContentStyled>
          {enabledSearch && (
            <Box className="selectFilter-search">
              <SearchBar
                fullWidth
                search={searchValue}
                onSearch={handleSearchChange}
              />
            </Box>
          )}
          {children}
          {loading ? (
            <SelectFilterLoading variant="captionMedium">
              {t('common.loading')}...
            </SelectFilterLoading>
          ) : (
            <>
              {visibleOptions.map((option) => (
                <Pressed
                  key={String(option.value)}
                  className="selectFilter-option"
                  onClick={() => handleOnChange(option)}
                >
                  <Typography
                    className="selectFilter-optionLabel"
                    variant="captionMedium"
                  >
                    {option.label}
                  </Typography>
                  {activeValues?.includes(option.value) && (
                    <Icon.Check color={Colors.blue500} />
                  )}
                </Pressed>
              ))}
              {filterOptions.length === 0 && (
                <Box className="selectFilter-option">
                  <Typography color={Colors.gray500} variant="bodyMedium">
                    {t('common.notFound')}
                  </Typography>
                </Box>
              )}
              <Box className="selectFilter-footer">
                {filterOptions.length > COUNT_VISIBLE_OPTIONS && (
                  <Pressed
                    className="selectFilter-footer-collapse"
                    onClick={() => setCollapsed((collapsed) => !collapsed)}
                  >
                    {collapsed ? t('common.collapse') : t('common.showAll')}
                  </Pressed>
                )}

                {activeValues.length > 0 && (
                  <Pressed
                    className="selectFilter-footer-clear"
                    onClick={() => onChange([])}
                  >
                    {t('common.resetAll')}
                  </Pressed>
                )}
              </Box>
            </>
          )}
        </PopoverContentStyled>
      </PopoverStyled>
    </>
  );
};
